import { useState, useEffect, useCallback } from 'react';
import { useApi } from './useApi';
import { useLoader } from '../context/loaders/useLoader';
import { useToast } from '../context/toasts/useToast';
import type { EpicBasic } from '../types/kanban';

export function useCardEpics(boardId: number, cardId: number) {
  const [epics, setEpics] = useState<EpicBasic[]>([]);
  const [error, setError] = useState<Error | null>(null);

  const { getEpics, addEpicToCard, removeEpicFromCard } = useApi();
  const { withLoader } = useLoader();
  const { addNotification } = useToast();

  const fetchEpics = useCallback(async () => {
    try {
      setError(null);
      const data = await getEpics(boardId);
      setEpics(data);
      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load epics';
      setError(err instanceof Error ? err : new Error(errorMessage));
      addNotification({
        type: 'error',
        title: 'Error Loading Epics',
        message: errorMessage,
        duration: 5000,
      });
      return [];
    }
  }, [getEpics, boardId, addNotification]);

  const addEpic = useCallback(
    async (epicId: number) => {
      try {
        setError(null);
        await withLoader(() => addEpicToCard(cardId, epicId), 'Linking epic...');
        addNotification({
          type: 'success',
          title: 'Epic Linked',
          message: 'Epic linked to card successfully.',
          duration: 3000,
        });
        return true;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to link epic';
        setError(err instanceof Error ? err : new Error(errorMessage));
        addNotification({
          type: 'error',
          title: 'Error Linking Epic',
          message: errorMessage,
          duration: 5000,
        });
        return false;
      }
    },
    [cardId, addEpicToCard, withLoader, addNotification]
  );

  const removeEpic = useCallback(
    async (epicId: number) => {
      try {
        setError(null);
        await withLoader(() => removeEpicFromCard(cardId, epicId), 'Unlinking epic...');
        addNotification({
          type: 'success',
          title: 'Epic Unlinked',
          message: 'Epic unlinked from card successfully.',
          duration: 3000,
        });
        return true;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to unlink epic';
        setError(err instanceof Error ? err : new Error(errorMessage));
        addNotification({
          type: 'error',
          title: 'Error Unlinking Epic',
          message: errorMessage,
          duration: 5000,
        });
        return false;
      }
    },
    [cardId, removeEpicFromCard, withLoader, addNotification]
  );

  useEffect(() => {
    fetchEpics();
  }, [fetchEpics]);

  return {
    epics,
    error, // For debugging, not for UI display
    loading: false, // Loading is handled by global loader
    fetchEpics,
    addEpic,
    removeEpic,
  };
}
